// Is a request's quantity a plain positive number?
//
// requests.quantity is free text the trader types ("100", "100 إلى 150 قطعة").
// Owner, 2026-09-30: when it is not a plain number, show it as written and show
// NO estimated total — never assume a quantity of 1.
//
// The test is the SAME one the database uses ('^[0-9]+(\.[0-9]+)?$' and > 0 in
// offer_amounts_sar_core, payment_quote and notify_buyer_offer_visible), so the
// screen hides a total exactly when the server's total would be built on 1.
// Latin digits only, like the SQL.
export function isPlainQuantity(quantity) {
  const t = String(quantity ?? '').trim();
  return /^[0-9]+(\.[0-9]+)?$/.test(t) && Number(t) > 0;
}

// Owner, 2026-09-30: on every trader form that creates or edits a request the
// quantity is REQUIRED and must pass isPlainQuantity — otherwise this message,
// and no submit. Payment multiplies price × quantity, and a text or empty
// quantity was being priced as 1.
//
// One copy of the wording, imported by every form. QUANTITY_MESSAGE is for a
// form with a unit selector next to the number; QUANTITY_MESSAGE_NO_UNIT for a
// form that has none (it must not point the trader at a field that is not
// there).
export const QUANTITY_MESSAGE = {
  ar: 'اكتب الكمية رقمًا فقط (مثال: 500). الوحدة تختارها من خانة الوحدة.',
  en: 'Enter the quantity as a number only (e.g. 500). Choose the unit in the unit field.',
  zh: '请只填写数字数量（例如 500），单位请在单位栏选择。',
};
export const QUANTITY_MESSAGE_NO_UNIT = {
  ar: 'اكتب الكمية رقمًا فقط (مثال: 500).',
  en: 'Enter the quantity as a number only (e.g. 500).',
  zh: '请只填写数字数量（例如 500）。',
};

export function quantityMessage(lang, { hasUnitField = true } = {}) {
  const table = hasUnitField ? QUANTITY_MESSAGE : QUANTITY_MESSAGE_NO_UNIT;
  return table[lang] || table.en;
}

// What a quantity input stores on every keystroke. It REWRITES the digits and
// decimal marks a trader's keyboard produces, and nothing else:
//   ٠-٩ (Arabic-Indic), ۰-۹ (Eastern Arabic / Persian), ０-９ (full-width, a
//   Chinese IME) → 0-9;   ٫ (Arabic decimal mark) and ． (full-width) → '.'
// It deliberately STRIPS NOTHING. The forms used to delete every non-digit as it
// was typed, which turned «100 إلى 150» into 100150 — a quantity the trader
// never asked for, then priced. Letters, spaces and ranges now stay visible and
// isPlainQuantity refuses them with QUANTITY_MESSAGE.
// Thousands separators (',' and '٬') are kept and refused too, not stripped:
// "1,5" means 1.5 to a European and 15 after a strip, and a silent ×10 on a
// priced quantity is worse than asking the trader to retype it.
const DIGIT_BLOCK_STARTS = [0x0660, 0x06f0, 0xff10];
export function normalizeQuantityInput(value) {
  return String(value ?? '').replace(/[\u0660-\u0669\u06f0-\u06f9\uff10-\uff19\u066b\uff0e]/g, (ch) => {
    if (ch === '\u066b' || ch === '\uff0e') return '.';
    const code = ch.charCodeAt(0);
    const start = DIGIT_BLOCK_STARTS.find((s) => code >= s && code <= s + 9);
    return String(code - start);
  });
}
